/**
 * 통합 설정 화면 (Phase 1)
 *
 * DBeaver Preferences 구조 차용 — 좌측 카테고리 + 우측 패널.
 * 값은 App.js 의 state 를 그대로 조작하고, 영속화는 App.js 의 useEffect
 * (saveSection) 가 담당한다. 즉 이 컴포넌트는 저장을 직접 하지 않는다.
 *
 * Phase 1: 흩어져 있던 설정을 한곳에 모으고 영속화.
 * Phase 2: 결과 그리드 표시 형식(NULL·날짜·천단위) + 실행 안전장치 추가.
 */
/* eslint-disable no-template-curly-in-string --
   스니펫 문법의 ${이름} 은 템플릿 리터럴이 아니라 '그대로 저장/표시해야 하는 문자열' 이다. */
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { FiSettings, FiEdit3, FiGrid, FiFolder, FiLayers, FiRotateCcw, FiPlay, FiAlignLeft, FiCode, FiPlus, FiTrash2, FiCommand, FiAlertTriangle, FiMessageSquare, FiUsers, FiUserCheck, FiTool, FiBookOpen } from 'react-icons/fi';
import { format as formatSql } from 'sql-formatter';
import { loadSnippets, saveSnippets, resetSnippets } from '../utils/sqlSnippets';
import { useConfirm } from './ConfirmDialog';
import AssistantSettingsSection from './assistant/AssistantSettingsSection';
import TierAdminSection from './assistant/TierAdminSection';
import UserGradeAdminSection from './UserGradeAdminSection';
import UserCommentSection from './UserCommentSection';
import { getMyTier } from '../services/assistantClient';
import { AI_ENABLED } from '../config/features';
import { groupBindings, findConflicts, SCOPE_LABEL_KEY } from '../utils/keyBindings';
import { resetSettings, ZOOM_MIN, ZOOM_MAX, DATE_FORMATS, PLAN_FORMATS, ACTUAL_PLAN_FORMATS, PLAN_STATS_METHODS, SQL_LANGUAGES, KEYWORD_CASES, OPERATOR_NEWLINES, CURSOR_SIZES, loadSection, saveSection } from '../utils/appSettings';
import { ZOOM_MIN as SCREEN_ZOOM_MIN, ZOOM_MAX as SCREEN_ZOOM_MAX, ZOOM_STEP as SCREEN_ZOOM_STEP } from '../utils/appZoom';
import i18n, { SUPPORTED_LANGUAGES, setLanguage } from '../i18n';
import AppModal from './common/AppModal';
import './SettingsModal.css';

/**
 * 티어 관리는 **관리자에게만 보인다.** 서버가 403 을 주는 것이 진짜 경계이고, 화면에서
 * 감추는 것은 편의다 — 반대로 하면(화면만 감추면) 새 경로가 생길 때마다 빠뜨린다.
 */
const getCategories = (t, isTierAdmin, isStandalone) => [
  { key: 'general', label: t('settings:cat.general'), icon: <FiSettings size={14} /> },
  { key: 'editor', label: t('settings:cat.editor'), icon: <FiEdit3 size={14} /> },
  { key: 'format', label: t('settings:cat.format'), icon: <FiAlignLeft size={14} /> },
  { key: 'snippets', label: t('settings:cat.snippets'), icon: <FiCode size={14} /> },
  { key: 'grid', label: t('settings:cat.grid'), icon: <FiGrid size={14} /> },
  { key: 'execution', label: t('settings:cat.execution'), icon: <FiPlay size={14} /> },
  // 사용자 주석(2026-10-29) — 코드 값의 뜻을 F2 · 결과 표에서. 정의는 서버에 주인별로
  { key: 'userComment', label: t('settings:cat.userComment'), icon: <FiBookOpen size={14} /> },
  { key: 'explorer', label: t('settings:cat.explorer'), icon: <FiFolder size={14} /> },
  { key: 'tabs', label: t('settings:cat.tabs'), icon: <FiLayers size={14} /> },
  // AI 제외 빌드에는 AI 설정이 없다 — 저장된 마지막 항목이 'assistant' 여도 아래 절이 그려지지 않는다
  ...(AI_ENABLED ? [{ key: 'assistant', label: t('settings:cat.assistant'), icon: <FiMessageSquare size={14} /> }] : []),
  { key: 'toolbar', label: t('settings:cat.toolbar'), icon: <FiTool size={14} /> },
  { key: 'keys', label: t('settings:cat.keys'), icon: <FiCommand size={14} /> },
  ...(isTierAdmin ? [{ key: 'tier', label: t('settings:cat.tier'), icon: <FiUsers size={14} /> }] : []),
  // 사용자 유형(2026-10-29) — 관리자만. 스탠드얼론은 로그인이 없어 유형도 없다
  ...(isTierAdmin && !isStandalone ? [{ key: 'userGrade', label: t('settings:cat.userGrade'), icon: <FiUserCheck size={14} /> }] : []),
];

/** 폰트 크기 슬라이더 — 영역별 줌(Ctrl+휠)과 같은 값을 공유한다. */
function FontSizeRow({ label, hint, value, onChange }) {
  return (
    <div className="stg-row">
      <div className="stg-row-main">
        <span className="stg-label">{label}</span>
        {hint && <span className="stg-hint">{hint}</span>}
      </div>
      <div className="stg-font-control">
        <input
          type="range"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={label}
        />
        <span className="stg-font-value">{value}px</span>
      </div>
    </div>
  );
}

/** 체크박스 한 줄 */
function CheckRow({ label, hint, checked, onChange }) {
  return (
    <label className="stg-row stg-row-clickable">
      <div className="stg-row-main">
        <span className="stg-label">{label}</span>
        {hint && <span className="stg-hint">{hint}</span>}
      </div>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

/** 숫자 입력 한 줄 */
function NumberRow({ label, hint, value, min, max, onChange }) {
  return (
    <div className="stg-row">
      <div className="stg-row-main">
        <span className="stg-label">{label}</span>
        {hint && <span className="stg-hint">{hint}</span>}
      </div>
      <input
        className="stg-number"
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    </div>
  );
}

/** 화면 확대/축소 선택지(%) — 머리줄 조절자 · Ctrl+휠과 같은 범위·간격(utils/appZoom) */
const SCREEN_ZOOMS = (() => {
  const out = [];
  for (let v = SCREEN_ZOOM_MIN; v <= SCREEN_ZOOM_MAX; v += SCREEN_ZOOM_STEP) out.push({ value: String(v), label: `${v}%` });
  return out;
})();
/** 실행계획 종류 — 실행계획 탭의 「옵션」(PlanOptionsMenu)과 같은 값·문구 */
const PLAN_MODES = [
  { value: 'estimated', labelKey: 'common:plo.estimated' },
  { value: 'actual', labelKey: 'common:plo.actual' },
];

/** 포매팅 미리보기용 샘플 — 대소문자·들여쓰기·AND 위치가 모두 드러나는 문장 */
const PREVIEW_SQL =
  "select e.emp_no, e.name, d.dept_name from employees e " +
  "join departments d on e.dept_id = d.id " +
  "where e.salary > 1000 and d.active = 'Y' order by e.name;";

/** 드롭다운 한 줄 */
function SelectRow({ label, hint, value, options, onChange }) {
  const { t } = useTranslation();
  // options 는 utils 의 모듈 스코프 상수라 문구 대신 키를 담고 있다.
  // 번역 대상이 아닌 항목('YYYY-MM-DD', 'SELECT' 등)은 label/hint 를 그대로 쓴다.
  const optLabel = (o) => (o.labelKey ? t(o.labelKey) : o.label);
  const optHint = (o) => (o.hintKey ? t(o.hintKey) : o.hint);
  return (
    <div className="stg-row">
      <div className="stg-row-main">
        <span className="stg-label">{label}</span>
        {hint && <span className="stg-hint">{hint}</span>}
      </div>
      <select className="stg-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(o => (
          <option key={o.value} value={o.value}>{optLabel(o)}{optHint(o) ? ` (${optHint(o)})` : ''}</option>
        ))}
      </select>
    </div>
  );
}

/** 짧은 텍스트 입력 한 줄 */
function TextRow({ label, hint, value, placeholder, onChange }) {
  return (
    <div className="stg-row">
      <div className="stg-row-main">
        <span className="stg-label">{label}</span>
        {hint && <span className="stg-hint">{hint}</span>}
      </div>
      <input
        className="stg-text"
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

const SettingsModal = ({
  isOpen, onClose, onApplyNow,
  darkMode, onToggleDarkMode,
  editorOptions, setEditorOptions,
  zoneZoom, setZoneZoom,
  tabKindFilter, setTabKindFilter,
  gridFormat, setGridFormat,
  execSettings, setExecSettings,
  sqlFormat, setSqlFormat,
  assistantSettings, setAssistantSettings, isStandalone,
  // 화면 확대/축소(%) — 머리줄 조절자와 같은 값(App 의 handleZoomChange)
  screenZoom, onZoomChange,
  initialCategory,
  onResetFavorites,
  // 도구 모음 — 메뉴에 없는 편집 조작(복사·붙여넣기 등)도 툴바에 넣고 뺄 수 있게
  toolbarActions = [],
  pinnedToolbarActions = [],
  onTogglePinnedAction,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [active, setActive] = useState('general');
  // 마우스 커서 크기 — 툴팁 위치(App 의 툴팁 컨트롤러가 띄울 때마다 읽는다). 이 화면만 쓰므로 여기서 읽고 쓴다
  const [cursorSize, setCursorSize] = useState(() => (loadSection('pointer') || {}).cursorSize || 'normal');
  const changeCursorSize = (v) => { setCursorSize(v); saveSection('pointer', { ...(loadSection('pointer') || {}), cursorSize: v }); };

  // 어디를 고치라는 건지 알 수 있게, 부른 쪽이 항목을 지정할 수 있다.
  // **열릴 때만** 반영한다 — 열려 있는 동안 사용자가 옮긴 항목을 되돌리면 안 된다.
  useEffect(() => {
    if (isOpen && initialCategory) setActive(initialCategory);
  }, [isOpen, initialCategory]);
  // 관리자인지는 **서버에 물어서** 안다 — `/tier/me` 의 isTierAdmin. 관리자 API 를 불러 보고 403 으로 아는 방식은
  // 비관리자가 페이지를 열 때마다 403 이 에러 로그에 쌓여(하루 600건) 바꿨다(2026-10-31). 진짜 경계는 서버의 403 그대로.
  const [isTierAdmin, setIsTierAdmin] = useState(false);
  useEffect(() => {
    let alive = true;
    getMyTier()
      .then((d) => { if (alive) setIsTierAdmin(!!(d && d.isTierAdmin)); })
      .catch((err) => { console.warn('[settings] 티어 관리 권한을 확인하지 못했어요 — 메뉴를 숨깁니다:', err); if (alive) setIsTierAdmin(false); });
    return () => { alive = false; };
  }, []);
  // SQL 템플릿 — SQLEditor 가 loadSnippets() 로 직접 읽으므로 저장만 하면 즉시 반영된다.
  const [snippets, setSnippets] = useState(() => loadSnippets());
  const [selSnippet, setSelSnippet] = useState(0);

  // 단축키 목록은 정적이라 매 렌더 재계산할 필요가 없다.
  const groupedKeys = useMemo(() => groupBindings(), []);
  const keyConflicts = useMemo(() => findConflicts(), []);

  // 포매팅 미리보기 — 옵션 조합이 잘못되면 메시지를 그대로 보여준다(설정 화면에서 바로 알 수 있게)
  const formatPreview = useMemo(() => {
    const f = sqlFormat || {};
    try {
      return formatSql(PREVIEW_SQL, {
        language: f.language || 'plsql',
        keywordCase: f.keywordCase || 'upper',
        tabWidth: Number(f.tabWidth) || 4,
        linesBetweenQueries: Number(f.linesBetweenQueries) || 0,
        logicalOperatorNewline: f.logicalOperatorNewline || 'before',
        expressionWidth: Number(f.expressionWidth) || 50,
        denseOperators: !!f.denseOperators,
        newlineBeforeSemicolon: !!f.newlineBeforeSemicolon,
      });
    } catch (e) {
      return t('settings:format.previewFailed') + ' ' + e.message;
    }
  }, [sqlFormat]);

  const setZoom = (zone, v) => setZoneZoom(prev => ({ ...prev, [zone]: v }));
  const setOpt = (key, v) => setEditorOptions(prev => ({ ...prev, [key]: v }));
  const setTab = (key, v) => setTabKindFilter(prev => ({ ...prev, [key]: v }));
  const setFmt = (key, v) => setGridFormat(prev => ({ ...prev, [key]: v }));
  const setExec = (key, v) => setExecSettings(prev => ({ ...prev, [key]: v }));
  const setFm = (key, v) => setSqlFormat(prev => ({ ...prev, [key]: v }));

  const commitSnippets = (next) => { setSnippets(next); saveSnippets(next); };
  const patchSnippet = (idx, key, v) => {
    commitSnippets(snippets.map((sn, i) => (i === idx ? { ...sn, [key]: v } : sn)));
  };
  const addSnippet = () => {
    const next = [...snippets, { label: 'new', detail: '', template: 'SELECT * FROM ${table}' }];
    commitSnippets(next);
    setSelSnippet(next.length - 1);
  };
  const removeSnippet = (idx) => {
    const next = snippets.filter((_, i) => i !== idx);
    commitSnippets(next);
    setSelSnippet(Math.max(0, Math.min(idx, next.length - 1)));
  };

  // 기본값 복원 — 저장소를 비우고 state 도 기본값으로 되돌린다.
  // 테마는 별도 키(livesql_theme)라 여기서 건드리지 않는다 (사용자 혼란 방지).
  // 확인 없이 두면 실수 클릭 한 번에 전 설정이 날아간다 (실제로 자동화 순회 클릭이
  // 초기화를 유발했다). 복구 불가한 파괴적 액션이므로 ConfirmDialog 를 거친다.
  const handleReset = async () => {
    const ok = await confirm({
      title: t('settings:resetConfirmTitle'),
      message: t('settings:resetConfirmMessage'),
      confirmText: t('settings:resetAll'),
      variant: 'danger',
    });
    if (!ok) return;
    const d = resetSettings();
    setZoneZoom(d.zoneZoom);
    setEditorOptions(d.editorOptions);
    setTabKindFilter(d.tabKindFilter);
    setGridFormat(d.gridFormat);
    setExecSettings(d.execution);
    setSqlFormat(d.sqlFormat);
    setCursorSize(d.pointer.cursorSize);
    // 메뉴 즐겨찾기(툴바에 붙인 것 · 숨긴 화면 버튼)도 함께 되돌린다
    if (onResetFavorites) onResetFavorites();
  };

  return (
    // 창 틀은 공용 AppModal(styles/appModal.css). 2026-11-04 사용자 요청으로 팝업이 아니라 **작업 탭**(windowed — 편집기 자리)이다.
    // 탭 제목 = 메뉴 문구(menu:file.appSettings) — 메뉴에서 다시 고르면 activateByTitle 이 이 탭을 앞으로 부른다
    <AppModal windowed noPopout half
      open={!!isOpen}
      onClose={onClose}
      title={t('menu:file.appSettings')}
      icon={<FiSettings size={14} />}
      className="stg-modal"
      overlayClassName="stg-overlay"
      closeLabel={t('settings:close')}
    >
        <div className="stg-body">
          <nav className="stg-nav">
            {getCategories(t, isTierAdmin, isStandalone).map(c => (
              <button
                key={c.key}
                className={active === c.key ? 'stg-nav-item active' : 'stg-nav-item'}
                onClick={() => setActive(c.key)}
              >
                <span className="stg-nav-icon">{c.icon}</span>{c.label}
              </button>
            ))}
          </nav>

          <div className="stg-panel">
            {active === 'general' && (
              <section>
                <h3>{t('settings:general.appearance')}</h3>
                <CheckRow
                  label={t('settings:general.darkMode')}
                  hint={t('settings:general.darkModeHint')}
                  checked={darkMode}
                  onChange={() => onToggleDarkMode && onToggleDarkMode()}
                />
                <SelectRow
                  label={t('settings:general.cursor.label')}
                  hint={t('settings:general.cursor.hint')}
                  value={cursorSize}
                  options={CURSOR_SIZES}
                  onChange={changeCursorSize}
                />
                {/* 언어·화면 확대/축소 — 머리줄에도 있는 것과 같은 값이다(2026-10-29, 설정에서도 찾을 수 있게) */}
                <SelectRow
                  label={t('settings:general.language')}
                  hint={t('settings:general.languageHint')}
                  value={(i18n.language || 'ko').split('-')[0]}
                  options={SUPPORTED_LANGUAGES.map(l => ({ value: l.code, label: l.label }))}
                  onChange={setLanguage}
                />
                {onZoomChange && (
                  <SelectRow
                    label={t('settings:general.screenZoom')}
                    hint={t('settings:general.screenZoomHint')}
                    value={String(screenZoom || 100)}
                    options={SCREEN_ZOOMS}
                    onChange={(v) => onZoomChange(Number(v))}
                  />
                )}
                {/* 영역 글자 크기는 기본값이고 화면 확대/축소가 그 위에 곱해진다(utils/appZoom) — 예전엔 Ctrl+휠이 이 값을 바꿨다 */}
                <p className="stg-note">{t('settings:general.zoomNote')}</p>
              </section>
            )}

            {active === 'editor' && (
              <section>
                <h3>{t('settings:cat.editor')}</h3>
                <CheckRow
                  label={t('settings:editor.autocomplete')}
                  hint={t('settings:editor.autocompleteHint')}
                  checked={editorOptions.autocomplete}
                  onChange={(v) => setOpt('autocomplete', v)}
                />
                <CheckRow
                  label={t('settings:editor.showComments')}
                  hint={t('settings:editor.showCommentsHint')}
                  checked={editorOptions.showComments}
                  onChange={(v) => setOpt('showComments', v)}
                />
                <CheckRow
                  label={t('settings:editor.columnSelection')}
                  hint={t('settings:editor.columnSelectionHint')}
                  checked={editorOptions.columnSelection}
                  onChange={(v) => setOpt('columnSelection', v)}
                />
                <h3>{t('settings:general.fontSize')}</h3>
                <FontSizeRow
                  label={t('settings:editor.fontSize')}
                  value={zoneZoom.editor}
                  onChange={(v) => setZoom('editor', v)}
                />
              </section>
            )}

            {active === 'format' && (
              <section>
                <h3>{t('settings:format.rules')}</h3>
                <SelectRow
                  label={t('settings:format.language')}
                  hint={t('settings:format.languageHint')}
                  value={sqlFormat.language}
                  options={SQL_LANGUAGES}
                  onChange={(v) => setFm('language', v)}
                />
                <SelectRow
                  label={t('settings:format.keywordCase')}
                  value={sqlFormat.keywordCase}
                  options={KEYWORD_CASES}
                  onChange={(v) => setFm('keywordCase', v)}
                />
                <NumberRow
                  label={t('settings:format.tabWidth')}
                  value={sqlFormat.tabWidth}
                  min={1}
                  max={8}
                  onChange={(v) => setFm('tabWidth', v)}
                />
                <SelectRow
                  label={t('settings:format.operatorNewline')}
                  hint={t('settings:format.operatorNewlineHint')}
                  value={sqlFormat.logicalOperatorNewline}
                  options={OPERATOR_NEWLINES}
                  onChange={(v) => setFm('logicalOperatorNewline', v)}
                />
                <NumberRow
                  label={t('settings:format.linesBetween')}
                  value={sqlFormat.linesBetweenQueries}
                  min={0}
                  max={5}
                  onChange={(v) => setFm('linesBetweenQueries', v)}
                />
                <NumberRow
                  label={t('settings:format.expressionWidth')}
                  hint={t('settings:format.expressionWidthHint')}
                  value={sqlFormat.expressionWidth}
                  min={20}
                  max={200}
                  onChange={(v) => setFm('expressionWidth', v)}
                />
                <CheckRow
                  label={t('settings:format.denseOperators')}
                  hint="x = 1 → x=1"
                  checked={sqlFormat.denseOperators}
                  onChange={(v) => setFm('denseOperators', v)}
                />
                <CheckRow
                  label={t('settings:format.newlineBeforeSemicolon')}
                  checked={sqlFormat.newlineBeforeSemicolon}
                  onChange={(v) => setFm('newlineBeforeSemicolon', v)}
                />

                <h3>{t('settings:format.preview')}</h3>
                <pre className="stg-preview">{formatPreview}</pre>
              </section>
            )}

            {active === 'snippets' && (
              <section>
                <h3>{t('settings:snippets.list')}</h3>
                <p className="stg-note stg-note-lead">
                  {t('settings:snippets.intro')}{' '}
                  {t('settings:snippets.placeholderNote1')} <code>{t('settings:snippets.placeholderSyntax')}</code>{' '}
                  {t('settings:snippets.placeholderNote2')} <strong>Tab</strong> {t('settings:snippets.placeholderNote3')}
                </p>

                <div className="stg-snippet-list">
                  {snippets.map((sn, i) => (
                    <button
                      key={i}
                      type="button"
                      className={i === selSnippet ? 'stg-snippet-item active' : 'stg-snippet-item'}
                      onClick={() => setSelSnippet(i)}
                    >
                      <code>{sn.label}</code>
                      <span className="stg-snippet-detail">{sn.detail}</span>
                    </button>
                  ))}
                  {snippets.length === 0 && (
                    <div className="stg-snippet-empty">{t('settings:snippets.empty')}</div>
                  )}
                </div>

                <div className="stg-snippet-actions">
                  <button className="app-btn" onClick={addSnippet}>
                    <FiPlus size={12} />{t('settings:snippets.add')}
                  </button>
                  <button
                    className="app-btn"
                    onClick={() => removeSnippet(selSnippet)}
                    disabled={snippets.length === 0}
                  >
                    <FiTrash2 size={12} />{t('settings:snippets.delete')}
                  </button>
                  <div className="stg-spacer" />
                  <button
                    className="app-btn"
                    onClick={() => { const d = resetSnippets(); setSnippets(d); setSelSnippet(0); }}
                    title={t('settings:snippets.resetHint')}
                  >
                    {t('settings:resetAll')}
                  </button>
                </div>

                {snippets[selSnippet] && (
                  <>
                    <h3>{t('settings:snippets.selected')}</h3>
                    <TextRow
                      label={t('settings:snippets.label')}
                      hint={t('settings:snippets.labelHint')}
                      value={snippets[selSnippet].label}
                      placeholder="sf"
                      onChange={(v) => patchSnippet(selSnippet, 'label', v)}
                    />
                    <TextRow
                      label={t('settings:snippets.detail')}
                      hint={t('settings:snippets.detailHint')}
                      value={snippets[selSnippet].detail}
                      placeholder="SELECT * FROM"
                      onChange={(v) => patchSnippet(selSnippet, 'detail', v)}
                    />
                    <div className="stg-row stg-row-block">
                      <div className="stg-row-main">
                        <span className="stg-label">{t('settings:snippets.template')}</span>
                        <span className="stg-hint">{t('settings:snippets.templateHint', { syntax: t('settings:snippets.placeholderSyntax') })}</span>
                      </div>
                      <textarea
                        className="stg-snippet-template"
                        value={snippets[selSnippet].template}
                        onChange={(e) => patchSnippet(selSnippet, 'template', e.target.value)}
                        rows={6}
                        spellCheck={false}
                      />
                    </div>
                  </>
                )}
              </section>
            )}

            {active === 'grid' && (
              <section>
                <h3>{t('settings:cat.grid')}</h3>
                <CheckRow
                  label={t('settings:grid.compact')}
                  hint={t('settings:grid.compactHint')}
                  checked={editorOptions.compactGrid}
                  onChange={(v) => setOpt('compactGrid', v)}
                />
                <h3>{t('settings:grid.format')}</h3>
                <TextRow
                  label={t('settings:grid.nullText')}
                  hint={t('settings:grid.nullTextHint')}
                  value={gridFormat.nullText}
                  placeholder="[NULL]"
                  onChange={(v) => setFmt('nullText', v)}
                />
                <SelectRow
                  label={t('settings:grid.dateFormat')}
                  hint={t('settings:grid.displayOnlyNote')}
                  value={gridFormat.dateFormat}
                  options={DATE_FORMATS}
                  onChange={(v) => setFmt('dateFormat', v)}
                />
                <CheckRow
                  label={t('settings:grid.numberGrouping')}
                  hint={t('settings:grid.numberGroupingHint')}
                  checked={gridFormat.numberGrouping}
                  onChange={(v) => setFmt('numberGrouping', v)}
                />
                <h3>{t('settings:general.fontSize')}</h3>
                <FontSizeRow
                  label={t('settings:cat.grid')}
                  value={zoneZoom.result}
                  onChange={(v) => setZoom('result', v)}
                />
              </section>
            )}

            {active === 'execution' && (
              <section>
                <h3>{t('settings:exec.confirm')}</h3>
                <CheckRow
                  label={t('settings:exec.confirmOnProd')}
                  hint={t('settings:exec.confirmOnProdHint')}
                  checked={execSettings.confirmOnProd}
                  onChange={(v) => setExec('confirmOnProd', v)}
                />
                <CheckRow
                  label={t('settings:exec.warnMissingWhere')}
                  hint={t('settings:exec.warnMissingWhereHint')}
                  checked={execSettings.warnMissingWhere}
                  onChange={(v) => setExec('warnMissingWhere', v)}
                />
                <p className="stg-note">
                  {t('settings:exec.connTypeNote1')} <strong>{t('settings:exec.connTypeNote2')}</strong> {t('settings:exec.connTypeNote3')}{' '}
                  {t('settings:exec.connTypeNote4')}
                </p>
                <h3>{t('settings:exec.plan')}</h3>
                <TextRow
                  label={t('settings:exec.planTable')}
                  hint={t('settings:exec.planTableHint')}
                  value={execSettings.planTable || ''}
                  placeholder="SYS.PLAN_TABLE$"
                  onChange={(v) => setExec('planTable', v)}
                />
                {/* 예상 실행계획을 어떻게 뜨는가 — EXPLAIN PLAN 으로 표에 담고 DBMS_XPLAN.DISPLAY 로 읽는다. 바꿀 수 있는 것은 표시 형식이다 */}
                {/* 종류·실제 형식·실측 수집·SQL Monitor — 실행계획 탭의 「옵션」 과 같은 값(2026-10-26 부터 「실행」 설정에 저장, 여기서도 고친다 2026-10-29) */}
                <SelectRow
                  label={t('settings:exec.planMode')}
                  hint={t(execSettings.planMode === 'actual' ? 'common:plo.actualNote' : 'common:plo.estimatedNote')}
                  value={execSettings.planMode === 'actual' ? 'actual' : 'estimated'}
                  options={PLAN_MODES}
                  onChange={(v) => setExec('planMode', v)}
                />
                <SelectRow
                  label={t('settings:exec.planFormat')}
                  hint={t('settings:exec.planFormatHint')}
                  value={execSettings.planFormat || 'TYPICAL'}
                  options={PLAN_FORMATS}
                  onChange={(v) => setExec('planFormat', v)}
                />
                <SelectRow
                  label={t('settings:exec.planActualFormat')}
                  hint={t('settings:exec.planActualFormatHint')}
                  value={execSettings.planActualFormat || 'ALLSTATS LAST'}
                  options={ACTUAL_PLAN_FORMATS.map(f => ({ value: f, label: f }))}
                  onChange={(v) => setExec('planActualFormat', v)}
                />
                <SelectRow
                  label={t('common:plo.stats')}
                  hint={t('settings:exec.planStatsHint')}
                  value={execSettings.planStatsMethod || 'session'}
                  options={PLAN_STATS_METHODS.map(m => ({ value: m, labelKey: `common:plo.statsMethod.${m}` }))}
                  onChange={(v) => setExec('planStatsMethod', v)}
                />
                <CheckRow
                  label={t('common:tun.monitorReportOpt')}
                  hint={t('common:tun.monitorReportHint')}
                  checked={execSettings.planMonitorReport}
                  onChange={(v) => setExec('planMonitorReport', v)}
                />
                <p className="stg-note">{t('settings:exec.planOptionsNote')}</p>
                <p className="stg-note">{t('settings:exec.planMethodNote')}</p>
              </section>
            )}

            {active === 'explorer' && (
              <section>
                <h3>{t('settings:cat.explorer')}</h3>
                <FontSizeRow
                  label={t('settings:explorer.fontSize')}
                  hint={t('settings:explorer.fontSizeHint')}
                  value={zoneZoom.explorer}
                  onChange={(v) => setZoom('explorer', v)}
                />
                {/* 「패널을 열 때 탐색기 접기」 는 없앴다(2026-10-19) — 탐색기는 스스로 접히지 않는다 */}
              </section>
            )}

            {active === 'tabs' && (
              <section>
                <h3>{t('settings:tabs.kindFilter')}</h3>
                <CheckRow
                  label={t('settings:tabs.queryResult')}
                  checked={tabKindFilter.result}
                  onChange={(v) => setTab('result', v)}
                />
                <CheckRow
                  label={t('settings:tabs.plan')}
                  checked={tabKindFilter.plan}
                  onChange={(v) => setTab('plan', v)}
                />
                <CheckRow
                  label={t('settings:tabs.objectInfo')}
                  checked={tabKindFilter.tableInfo}
                  onChange={(v) => setTab('tableInfo', v)}
                />
                <h3>{t('settings:general.fontSize')}</h3>
                {/* 실행계획 글자는 편집기 글자 크기를 따른다(2026-10-26) — 따로 고르지 않는다 */}
                <p className="stg-note">{t('settings:general.planFollowsEditor')}</p>
                <FontSizeRow
                  label={t('settings:tabs.objectInfo')}
                  value={zoneZoom.tableInfo}
                  onChange={(v) => setZoom('tableInfo', v)}
                />
              </section>
            )}

            {AI_ENABLED && active === 'assistant' && (
              <section>
                <h3>{t('settings:cat.assistant')}</h3>
                <AssistantSettingsSection
                  settings={assistantSettings || {}}
                  setSettings={setAssistantSettings}
                  isStandalone={isStandalone}
                />
              </section>
            )}

            {active === 'userComment' && (
              <section>
                <h3>{t('settings:cat.userComment')}</h3>
                <UserCommentSection />
              </section>
            )}

            {active === 'userGrade' && isTierAdmin && !isStandalone && (
              <section>
                <h3>{t('settings:cat.userGrade')}</h3>
                <UserGradeAdminSection />
              </section>
            )}

            {active === 'tier' && isTierAdmin && (
              <section>
                <h3>{t('settings:cat.tier')}</h3>
                <TierAdminSection />
              </section>
            )}

            {active === 'toolbar' && (
              <section>
                <h3>{t('settings:cat.toolbar')}</h3>
                <p className="stg-note stg-note-lead">
                  {t('settings:toolbar.intro')}
                </p>
                {toolbarActions.map(action => (
                  <CheckRow
                    key={action.key}
                    label={action.label}
                    checked={pinnedToolbarActions.includes(action.key)}
                    onChange={() => onTogglePinnedAction && onTogglePinnedAction(action.key)}
                  />
                ))}
              </section>
            )}

            {active === 'keys' && (
              <section>
                <h3>{t('settings:cat.keys')}</h3>
                <p className="stg-note stg-note-lead">
                  {t('settings:keys.intro1')} <strong>{t('settings:keys.introStrong')}</strong> —{' '}
                  {t('settings:keys.intro2')}
                </p>

                {Object.keys(keyConflicts).length > 0 && (
                  <div className="stg-conflict">
                    <FiAlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
                    {t('settings:keys.conflict')}{' '}
                    {Object.keys(keyConflicts).join(', ')} {t('settings:keys.conflictHint')}
                  </div>
                )}

                {groupedKeys.map(g => (
                  <div key={g.group} className="stg-key-group">
                    <h3>{t(g.group)}</h3>
                    <table className="stg-key-table">
                      <tbody>
                        {g.items.map((b, i) => (
                          <tr key={i} className={keyConflicts[b.key.toUpperCase()] ? 'conflict' : ''}>
                            {/* key 는 실제 조합이라 그대로 — 조합이 아닌 항목만 keyLabelKey 로 표시 문구를 준다 */}
                            <td className="stg-key-combo"><kbd>{b.keyLabelKey ? t(b.keyLabelKey) : b.key}</kbd></td>
                            <td className="stg-key-action">
                              {t(b.actionKey)}
                              {b.noteKey && <div className="stg-key-note">{t(b.noteKey)}</div>}
                            </td>
                            <td className="stg-key-scope">{t(SCOPE_LABEL_KEY[b.scope]) || b.scope}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </section>
            )}
          </div>
        </div>

        <div className="stg-footer app-modal-footer">
          <button className="app-btn" onClick={handleReset} title={t('settings:resetAllHint')}>
            <FiRotateCcw size={13} />
            {t('settings:resetAll')}
          </button>
          <div className="stg-spacer" />
          <span className="stg-saved">{t('settings:savedInstantly')}</span>
          {onApplyNow && (
            <button className="app-btn" onClick={onApplyNow} title={t('settings:applyNowHint')}>{t('settings:applyNow')}</button>
          )}
          <button className="app-btn app-btn-primary" onClick={onClose}>{t('settings:close')}</button>
        </div>
    </AppModal>
  );
};

SettingsModal.propTypes = {
  isOpen: PropTypes.bool,
  /** 열 때 바로 보여 줄 항목 (예: 'assistant'). 없으면 마지막에 보던 곳. */
  initialCategory: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  /** 「즉시 적용」 — 지금 값을 한꺼번에 저장하고 알린다 */
  onApplyNow: PropTypes.func,
  darkMode: PropTypes.bool,
  onToggleDarkMode: PropTypes.func,
  editorOptions: PropTypes.object.isRequired,
  setEditorOptions: PropTypes.func.isRequired,
  zoneZoom: PropTypes.object.isRequired,
  setZoneZoom: PropTypes.func.isRequired,
  tabKindFilter: PropTypes.object.isRequired,
  setTabKindFilter: PropTypes.func.isRequired,
  gridFormat: PropTypes.object.isRequired,
  setGridFormat: PropTypes.func.isRequired,
  execSettings: PropTypes.object.isRequired,
  setExecSettings: PropTypes.func.isRequired,
  sqlFormat: PropTypes.object.isRequired,
  setSqlFormat: PropTypes.func.isRequired,
  onResetFavorites: PropTypes.func,
  toolbarActions: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })),
  pinnedToolbarActions: PropTypes.arrayOf(PropTypes.string),
  onTogglePinnedAction: PropTypes.func,
  assistantSettings: PropTypes.object,
  setAssistantSettings: PropTypes.func,
  isStandalone: PropTypes.bool,
  screenZoom: PropTypes.number,
  onZoomChange: PropTypes.func,
};

export default SettingsModal;
